import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "./supabase";
import AddonsModal from "./AddonsModal";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

const stripePromise = loadStripe("pk_live_51TPSNN9gc0MT8Jg7aFesfPT0xlWbQ5fx1kHIpHqw1nwna52Z0sai0h5L0nHbQfO9kRII1On3Ag4ikZwO6zCJ9vuc00FIGFrxoh");

function DepositPaymentForm({ amount, description, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setErrorMsg("");

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.origin + "/book" },
      redirect: "if_required",
    });

    if (error) {
      setErrorMsg(error.message || "Payment failed");
      setProcessing(false);
      onError && onError(error.message);
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      onSuccess(paymentIntent);
    } else {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{marginBottom:16}}>
        <PaymentElement options={{layout:"tabs"}}/>
      </div>
      {errorMsg && <div style={{padding:"10px 14px",background:"rgba(192,57,43,0.1)",color:"#c0392b",borderRadius:8,fontSize:12,fontWeight:600,marginBottom:12}}>{errorMsg}</div>}
      <button type="submit" disabled={!stripe || processing} style={{width:"100%",padding:"14px",background:(!stripe||processing)?"#ccc":"linear-gradient(135deg,#b48c3c,#8a6f2e)",color:"#fff",border:"none",borderRadius:10,fontSize:15,fontWeight:700,cursor:(!stripe||processing)?"not-allowed":"pointer",fontFamily:"Montserrat,sans-serif"}}>
        {processing?"Processing Payment...":"💳 Pay $"+amount}
      </button>
    </form>
  );
}

// ★ Palette matched to novamedicalspa.com (2026-06-15) — dark luxury theme.
//   Extracted from the live site CSS: near-black background (#0a0709 →
//   #15101a gradient), gold accents (#c9a36d / #d4b896 / #e8d4b8), cream
//   text (#f5f1ea), Fraunces serif headings + Manrope body.
// ★ Refined dark-luxury palette (2026-06-15 v2). The first pass used the
//   site's literal near-black (#0a0709) behind pure-white cards — too harsh,
//   the cards looked like they were floating in a void. Luxury dark design
//   principle: NEVER pure/near black. Use a warm, deep, soft charcoal-plum
//   with a gold undertone, and warm the cards to ivory so the contrast is
//   refined rather than jarring. Everything shares a warm thread → cohesive.
const C = {
  // page background — warm deep charcoal-plum (lifted ~12% from the site's
  // near-black so white cards don't glare against it)
  pageBg:"#1d171f", pageBg2:"#2a212c",
  bg:"#211a23",
  // cards — warm ivory, not stark white (#fff vs #faf7f1 = night & day on dark)
  surface:"#faf7f1", cream:"#f5efe6",
  border:"#e6ded2", borderGold:"rgba(201,163,109,0.30)", goldBg:"rgba(201,163,109,0.10)",
  gold:"#c9a36d", goldD:"#9d8666", goldL:"#d4b896", goldLite:"#e8d4b8",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", purple:"#7c3aed", orange:"#e67e22",
  // text — warm near-black for inside the ivory cards
  text:"#2a2128", muted:"#6b6358", dim:"#9c9388", light:"#f3f4f6",
  // text that sits directly on the dark page background
  onDark:"#f5f1ea", onDarkMuted:"rgba(245,241,234,0.72)",
};
const F = "'Manrope','Montserrat',system-ui,sans-serif";
const S = "'Fraunces','Playfair Display','Cormorant Garamond',Georgia,serif";

// ★ "Specials" goes FIRST so promo/seasonal offers (e.g. Glass Skin Facial
//   $99) appear at the top of the public booking menu. Owner can flag any
//   service as Specials category in the ServicesManager and it'll bubble up
//   here automatically — no FE code change needed per promo.
const CAT_ORDER = ["Specials","Injectables","Facials","Skin Treatments","Laser Hair Removal","Laser Treatments","Health + Wellness","Weightloss","Hair Restoration","Treatment Add-ons","Memberships"];
const CAT_COLORS = {
  // ★ Specials recolored 2026-06-15 — the bright red (#e11d48) clashed with
  //   the gold/cream luxury palette. Deep rose-gold reads "premium offer"
  //   while flowing with the brand.
  "Specials":"#a8506a", // deep rose-gold — elegant promo accent
  "Injectables":"#b48c3c","Facials":"#c08552","Skin Treatments":"#7c6f9c",
  "Laser Hair Removal":"#b06a7a",
  "Laser Treatments":"#9c5c6e","Health + Wellness":"#6b8e7a","Weightloss":"#7a9b8e",
  "Hair Restoration":"#8e7aaa","Treatment Add-ons":"#9c8aae","Memberships":"#b8895a",
};

// Custom SVG icons (replace cartoony emojis)
const CatIcon = ({ cat, color, size = 26 }) => {
  const stroke = color;
  const props = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (cat) {
    case "Specials":
      // Star burst — universal "promo/deal" symbol
      return (<svg {...props}><path d="M12 2l2.4 5.2L20 8l-4 4.4 1 5.6-5-2.8L7 18l1-5.6L4 8l5.6-.8L12 2z" fill={stroke+"22"}/><path d="M12 2l2.4 5.2L20 8l-4 4.4 1 5.6-5-2.8L7 18l1-5.6L4 8l5.6-.8L12 2z"/></svg>);
    case "Injectables":
      return (<svg {...props}><path d="M19 3l2 2"/><path d="M16 6l4 4"/><path d="M15 7l-9 9-2 5 5-2 9-9"/><path d="M9 13l3 3"/></svg>);
    case "Facials":
      return (<svg {...props}><circle cx="12" cy="12" r="9"/><path d="M8 14c1 1.5 2.5 2.5 4 2.5s3-1 4-2.5"/><circle cx="9" cy="10" r=".7" fill={stroke}/><circle cx="15" cy="10" r=".7" fill={stroke}/><path d="M6 7l-1.5-1.5M18 7l1.5-1.5M5 12H3M21 12h-2"/></svg>);
    case "Skin Treatments":
      return (<svg {...props}><path d="M12 3c0 5-4 8-4 12a4 4 0 008 0c0-4-4-7-4-12z"/><path d="M10 14a2 2 0 002 2"/></svg>);
    case "Laser Hair Removal":
      // Razor — distinguishes hair removal from the lightning-bolt laser cat
      return (<svg {...props}><rect x="4" y="3" width="10" height="6" rx="1.5"/><path d="M9 9v5"/><path d="M6 17h6a2 2 0 002-2v-1H4v1a2 2 0 002 2z"/></svg>);
    case "Laser Treatments":
      return (<svg {...props}><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z"/></svg>);
    case "Health + Wellness":
      return (<svg {...props}><path d="M12 21s-7-4.5-7-10a4 4 0 017-2.6A4 4 0 0119 11c0 5.5-7 10-7 10z"/></svg>);
    case "Weightloss":
      return (<svg {...props}><rect x="3" y="6" width="18" height="13" rx="2"/><path d="M7 6V4M17 6V4"/><path d="M9 13l1.5 2 2-3 1.5 2 2-3"/></svg>);
    case "Hair Restoration":
      return (<svg {...props}><path d="M6 20c0-6 3-12 6-12s6 6 6 12"/><path d="M9 12c1-2 2-3 3-3s2 1 3 3"/><path d="M12 4v3"/></svg>);
    case "Treatment Add-ons":
      return (<svg {...props}><circle cx="12" cy="12" r="9"/><path d="M12 8v8M8 12h8"/></svg>);
    case "Memberships":
      return (<svg {...props}><path d="M12 2l3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z"/></svg>);
    default:
      return (<svg {...props}><circle cx="12" cy="12" r="9"/></svg>);
  }
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW_SHORT = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

// Email campaign that sent this visitor (?ec=14 on the Book Now link). Kept for
// the session so it survives moving between booking steps.
function emailCampaignId() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("ec");
    if (fromUrl && /^\d+$/.test(fromUrl)) sessionStorage.setItem("elyon_ec", fromUrl);
    const v = sessionStorage.getItem("elyon_ec");
    return v ? Number(v) : null;
  } catch (_) { return null; }
}

export default function PublicBooking() {
  emailCampaignId(); // remember ?ec before any step changes the URL
  const { slug } = useParams();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [bookingSettings, setBookingSettings] = useState({});
  const [showAddonsModal, setShowAddonsModal] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [providers, setProviders] = useState([]);
  const [weeklyHours, setWeeklyHours] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [providerServices, setProviderServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCategory, setOpenCategory] = useState(null);
  // ★ When set (via ?category= embed param) the menu shows ONLY this category.
  const [embedCategory, setEmbedCategory] = useState(null);
  const [calMonth, setCalMonth] = useState(new Date());
  
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [clientInfo, setClientInfo] = useState({ name:"", phone:"", email:"", notes:"" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showPayment, setShowPayment] = useState(false);
  const [paymentIntentSecret, setPaymentIntentSecret] = useState(null);
  const [pendingClientId, setPendingClientId] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]); // clinic-wide holidays / special hours
  const [providerTimeoff, setProviderTimeoff] = useState([]); // per-provider vacations / partial-day blocks
  const [providerExtra, setProviderExtra] = useState([]); // date-specific availability (guest providers / extra days)

  useEffect(() => {
    loadData();
    const chs = supabase.channel("public-booking-"+Math.random())
      .on("postgres_changes", { event: "*", schema: "public", table: "services" }, loadData)
      .on("postgres_changes", { event: "*", schema: "public", table: "providers" }, loadData)
      .on("postgres_changes", { event: "*", schema: "public", table: "provider_weekly_hours" }, loadData)
      .on("postgres_changes", { event: "*", schema: "public", table: "appointments" }, loadData)
      .on("postgres_changes", { event: "*", schema: "public", table: "provider_services" }, loadData)
      .on("postgres_changes", { event: "*", schema: "public", table: "booking_blocked_dates" }, loadData)
      .on("postgres_changes", { event: "*", schema: "public", table: "provider_timeoff" }, loadData)
      .on("postgres_changes", { event: "*", schema: "public", table: "provider_extra_availability" }, loadData)
      .subscribe();
    return () => { supabase.removeChannel(chs); };
  }, []);

  // ★ Supabase caps every response at 1000 rows. Loading all appointments in
  //   one call silently dropped ~80% of them, so booked slots showed as open
  //   and clients paid a deposit for a slot that was then refused (Stacey W,
  //   2026-09-21: charged 5x, never booked). Load only today onward, paged.
  const loadUpcomingAppointments = async () => {
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    const all = [];
    for (let from = 0; ; from += 1000) {
      const { data, error } = await supabase.from("appointments").select("id,pid,date,time,dur,status")
        .neq("status","cancelled").gte("date", today).order("id").range(from, from + 999);
      if (error) return { data: all, error };
      all.push(...(data || []));
      if (!data || data.length < 1000) return { data: all };
    }
  };

  const loadData = async () => {
    const [svc, prov, wh, appts, ps, bs, bd, pto, pex] = await Promise.all([
      supabase.from("services").select("*").eq("active", true).order("category").order("name"),
      supabase.from("providers").select("*").eq("active", true).order("order_index").order("name"),
      supabase.from("provider_weekly_hours").select("*").eq("available", true),
      loadUpcomingAppointments(),
      supabase.from("provider_services").select("*"),
      supabase.from("booking_settings").select("*").eq("id", 1).maybeSingle(),
      supabase.from("booking_blocked_dates").select("*"),
      supabase.from("provider_timeoff").select("*"),
      supabase.from("provider_extra_availability").select("*"),
    ]);
    // ★ Strip retail/product categories from the public booking flow —
    //   clients can't book a face cream as an appointment slot. Retail
    //   categories are identified by the "Retail" prefix on the category
    //   name (Retail — Hydrinity, Retail — IS Clinical, etc.).
    setServices((svc.data || []).filter(s => !/^retail/i.test(String(s.category || "").trim())));
    setProviders(prov.data || []);
    setWeeklyHours(wh.data || []);
    setAppointments(appts.data || []);
    setProviderServices(ps.data || []);
    setBookingSettings(bs.data || {});
    setBlockedDates(bd.data || []);
    setProviderTimeoff(pto.data || []);
    setProviderExtra(pex.data || []);
    setLoading(false);

    // ★ DEEP-LINK / EMBED SUPPORT — a per-service embed links to
    //   /book?service=<id|name>. Auto-select that service and jump the
    //   client straight to step 2 (date/provider), so they never have to
    //   navigate the menu. Falls back silently to the normal flow if the
    //   param is missing or doesn't match anything.
    try {
      const params = new URLSearchParams(window.location.search);
      const wanted = params.get("service");
      const wantedCat = params.get("category");
      const list = (svc.data || []).filter(s => !/^retail/i.test(String(s.category || "").trim()));
      const norm = (x) => String(x || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      if (wanted) {
        const match = list.find(s => String(s.id) === wanted)
          || list.find(s => norm(s.name) === norm(wanted))
          || list.find(s => norm(s.name).includes(norm(wanted)));
        if (match) {
          setSelectedService(match);
          setStep(2);
        }
      } else if (wantedCat) {
        // ★ CATEGORY DEEP-LINK — an embed like /book?category=Laser%20Hair%20Removal
        //   opens step 1 with ONLY that category expanded, so the client
        //   sees just those treatments. We stash it; the render reads
        //   `embedCategory` to auto-open + filter.
        const catMatch = Object.keys(
          list.reduce((a, s) => { a[s.category || ""] = 1; return a; }, {})
        ).find(c => norm(c) === norm(wantedCat) || norm(c).includes(norm(wantedCat)));
        if (catMatch) {
          setEmbedCategory(catMatch);
          setOpenCategory(catMatch);
        }
      }
    } catch (e) { /* embed param best-effort */ }
  };

  const servicesByCategory = {};
  services.forEach(s => {
    if (!servicesByCategory[s.category]) servicesByCategory[s.category] = [];
    servicesByCategory[s.category].push(s);
  });

  // Provider time off for a given date: { wholeDay, ranges:[{s,e} in minutes] }.
  // A row with no start_time/end_time blocks the whole day; with hours it blocks
  // only that window. Applies across [start_date, end_date].
  const localDateStr = (d) => {
    const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,"0"), day = String(d.getDate()).padStart(2,"0");
    return `${y}-${m}-${day}`;
  };
  const providerOff = (providerId, dateStr) => {
    const offs = (providerTimeoff || []).filter(t =>
      t.provider_id === providerId && dateStr >= t.start_date && dateStr <= (t.end_date || t.start_date));
    const wholeDay = offs.some(t => !t.start_time || !t.end_time);
    const ranges = offs.filter(t => t.start_time && t.end_time).map(t => {
      const [sh, sm] = t.start_time.split(":").map(Number);
      const [eh, em] = t.end_time.split(":").map(Number);
      return { s: sh*60+sm, e: eh*60+em };
    });
    return { wholeDay, ranges };
  };

  // Date-specific extra availability (guest providers / one-off extra days).
  // A row here makes the provider bookable in that window on that date even
  // with no weekly schedule (e.g. Liz covering specific September dates).
  const extraFor = (providerId, dateStr) =>
    (providerExtra || []).filter(x => x.provider_id === providerId && x.date === dateStr);

  const dayHasAvailability = (d) => {
    const today = new Date();
    today.setHours(0,0,0,0);
    if (d < today) return false;
    if (!selectedService) return false;
    const dow = d.getDay();
    const dateStr = localDateStr(d);
    if ((blockedDates || []).some(b => b.date === dateStr && b.closed)) return false;
    const qualifiedIds = providerServices.filter(ps => ps.service_id === selectedService.id).map(ps => ps.provider_id);
    return providers.some(p =>
      qualifiedIds.includes(p.id) &&
      (weeklyHours.some(h => h.provider_id === p.id && h.day_of_week === dow) || extraFor(p.id, dateStr).length > 0) &&
      !providerOff(p.id, dateStr).wholeDay
    );
  };

  const getProvidersForDate = () => {
    if (!selectedDate || !selectedService) return [];
    const dow = selectedDate.getDay();
    const dateStr = localDateStr(selectedDate);
    const qualifiedIds = providerServices.filter(ps => ps.service_id === selectedService.id).map(ps => ps.provider_id);
    return providers.filter(p =>
      qualifiedIds.includes(p.id) &&
      (weeklyHours.some(h => h.provider_id === p.id && h.day_of_week === dow) || extraFor(p.id, dateStr).length > 0) &&
      !providerOff(p.id, dateStr).wholeDay
    );
  };

  const getAvailableTimes = () => {
    if (!selectedProvider || !selectedDate || !selectedService) return [];
    const dow = selectedDate.getDay();
    const dateStr = selectedDate.toISOString().split("T")[0];

    // ★ Clinic-wide date override (holiday closure or special hours).
    const override = (blockedDates || []).find(b => b.date === dateStr);
    if (override && override.closed) return []; // holiday — fully closed, no slots

    // ★ Per-provider time off. Whole-day → no slots; partial → block that window.
    const off = providerOff(selectedProvider.id, dateStr);
    if (off.wholeDay) return [];

    const duration = selectedService.duration_minutes || 60;
    const toMin = (t) => { const [h, m] = String(t).split(":").map(Number); return h * 60 + m; };

    // Build the provider's availability WINDOWS for this date:
    //  • weekly hours for this weekday (clinic special-hours override applies)
    //  • PLUS any date-specific extra availability (guest providers / extra days)
    const windows = [];
    const hours = weeklyHours.find(h => h.provider_id === selectedProvider.id && h.day_of_week === dow);
    if (hours) {
      const useSpecial = override && !override.closed && override.start_time && override.end_time;
      windows.push({
        s: toMin(useSpecial ? override.start_time : hours.start_time),
        e: toMin(useSpecial ? override.end_time : hours.end_time),
      });
    }
    for (const x of extraFor(selectedProvider.id, dateStr)) {
      windows.push({ s: toMin(x.start_time), e: toMin(x.end_time) });
    }
    if (windows.length === 0) return [];

    const booked = appointments.filter(a => a.pid === selectedProvider.id && a.date === dateStr);
    const seen = new Set();
    const slots = [];

    for (const w of windows) {
      for (let t = w.s; t + duration <= w.e; t += 30) {
        if (seen.has(t)) continue;
        const slotEnd = t + duration;
        const conflict = booked.some(b => {
          const [bH, bM] = b.time.split(":").map(Number);
          const bStart = bH * 60 + bM;
          const bEnd = bStart + (b.dur || 60);
          return (t < bEnd && slotEnd > bStart);
        });
        // Skip slots overlapping a partial-day time-off window.
        const inTimeOff = off.ranges.some(r => t < r.e && slotEnd > r.s);
        if (!conflict && !inTimeOff) {
          seen.add(t);
          const h = Math.floor(t / 60);
          const m = t % 60;
          const ampm = h >= 12 ? "PM" : "AM";
          const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
          slots.push({ value: `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`, display: `${h12}:${String(m).padStart(2,"0")} ${ampm}`, _t: t });
        }
      }
    }
    // Multiple windows can interleave — keep the list in time order.
    slots.sort((a, b) => a._t - b._t);
    return slots;
  };

  // Re-check the chosen slot against the LIVE calendar. The slot list on
  // screen is a snapshot and can be stale.
  const slotStillFree = async () => {
    const dateStr = selectedDate.toISOString().split("T")[0];
    const [h, m] = selectedTime.split(":").map(Number);
    const start = h * 60 + m, end = start + (selectedService.duration_minutes || 60);
    const { data: live, error } = await supabase.from("appointments")
      .select("time,dur,status")
      .eq("pid", selectedProvider.id).eq("date", dateStr).neq("status", "cancelled");
    if (error) throw new Error("We couldn't check availability. Please try again or call us at (516) 543-3940.");
    return !(live || []).some(b => {
      const [bh, bm] = (b.time || "0:0").split(":").map(Number);
      const bStart = bh * 60 + bm, bEnd = bStart + (b.dur || 60);
      return start < bEnd && end > bStart;
    });
  };

  const submit = async () => {
    if (!clientInfo.name || !clientInfo.phone || !clientInfo.email) {
      setErrorMsg("Please fill in name, phone, and email");
      return;
    }
    // Validate email shape on the client so we don't waste a round-trip
    // to Stripe (Stripe rejects malformed emails with a generic error).
    const emailRe = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    if (!emailRe.test(String(clientInfo.email).trim())) {
      setErrorMsg("Please enter a valid email address (e.g. you@example.com).");
      return;
    }
    // Validate phone has at least 10 digits (US) so Stripe doesn't reject it.
    const phoneDigits = String(clientInfo.phone || "").replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      setErrorMsg("Please enter a valid phone number (at least 10 digits).");
      return;
    }
    setSubmitting(true);
    setErrorMsg("");

    try {
      let clientId;
      let existingNoShow = 0;
      // Match by email AND phone so a returning client's noshow record is
      // surfaced even if they typed their email a little differently this
      // time. Whichever match comes back first wins.
      let { data: existing } = await supabase.from("clients").select("id,noshow,name").eq("email", clientInfo.email).maybeSingle();
      if (!existing) {
        const last10 = (clientInfo.phone || "").replace(/\D/g, "").slice(-10);
        if (last10) {
          const { data: byPhone } = await supabase.from("clients").select("id,noshow,name").like("phone", `%${last10}%`).limit(1);
          existing = byPhone?.[0] || null;
        }
      }
      if (existing) {
        clientId = existing.id;
        existingNoShow = Number(existing.noshow) || 0;
      } else {
        const newClient = {
          id: Date.now(), name: clientInfo.name, phone: clientInfo.phone, email: clientInfo.email,
          dob: null, allergies: "None", med: "No contraindications",
          spent: 0, visits: 0, noshow: 0, tags: [], notes: "", status: "active",
          cardOnFile: false, gfeStatus: "none", loyaltyPts: 0, memberPlan: null,
          loc: (bookingSettings.spa_name || "Nova Med Spa")
        };
        const { error: ce } = await supabase.from("clients").insert([newClient]);
        if (ce) throw new Error("Failed to create client: " + ce.message);
        clientId = newClient.id;
      }

      const dateStr = selectedDate.toISOString().split("T")[0];
      // No-show clients ALWAYS pay a deposit. Take the larger of the
      // service's normal deposit and the $25 no-show floor.
      const NO_SHOW_DEPOSIT_MIN = 25;
      const baseDeposit = selectedService.requires_deposit ? (selectedService.deposit_amount || 0) : 0;
      const requiredDeposit = existingNoShow > 0
        ? Math.max(NO_SHOW_DEPOSIT_MIN, baseDeposit)
        : baseDeposit;
      const needsDeposit = requiredDeposit > 0;
      // Selectively replace the service's deposit amount for the payment
      // call so the Stripe intent matches what we charge. `selectedService`
      // is React state (const) — assigning to it throws and kills the booking
      // for returning no-show clients, so bump via a local + the setter (the
      // setter feeds the payment screen and createAppointment, which read
      // state on a later render; the local feeds this closure right now).
      let svc = selectedService;
      if (existingNoShow > 0 && requiredDeposit > baseDeposit) {
        svc = { ...selectedService, deposit_amount: requiredDeposit, requires_deposit: true };
        setSelectedService(svc);
      }

      // ★ Never charge for a slot that is already taken — check BEFORE the
      //   deposit, not only after it.
      if (!(await slotStillFree())) {
        loadData();
        setSelectedTime(null);
        throw new Error("Sorry — that time is no longer available. You have not been charged. Please choose another time.");
      }

      if (needsDeposit) {
        setPendingClientId(clientId);
        const { data, error } = await supabase.functions.invoke("stripe-payment", {
          body: {
            action: "create_payment_intent",
            // Edge function expects DOLLARS (it converts to cents internally
            // via Math.round(amount * 100) before sending to Stripe). Sending
            // cents here meant $25 was charged as $2,500.
            amount: Number(svc.deposit_amount),
            client_id: clientId,
            description: "Deposit for " + svc.name,
            metadata: {
              booking_type: "online_deposit",
              service: svc.name,
              provider: selectedProvider.name,
            }
          }
        });
        if (error || !data?.client_secret) {
          // Pull the real error body out of FunctionsHttpError so staff
          // can see WHY (e.g. "Invalid deposit amount", "Unauthorized")
          // instead of the cryptic "Edge Function returned a non-2xx".
          let detail = error?.message || "Unknown error";
          try {
            if (error?.context?.json) {
              const j = await error.context.json();
              if (j?.error) detail = j.error;
            } else if (data?.error) {
              detail = data.error;
            }
          } catch { /* ignore */ }
          // Log full diagnostic so we can debug from logs if a guest reports it
          console.error("[deposit] create_payment_intent failed", { error, data, detail });
          throw new Error("We couldn't start the deposit charge: " + detail + ". Please try again or call us at (516) 543-3940.");
        }
        setPaymentIntentSecret(data.client_secret);
        setShowPayment(true);
        setSubmitting(false);
        return;
      }

      await createAppointment(clientId, 0, null);
    } catch (e) {
      setErrorMsg(e.message);
      setSubmitting(false);
    }
  };

  const createAppointment = async (clientId, depositPaid, paymentIntentId) => {
    try {
      const dateStr = selectedDate.toISOString().split("T")[0];
      const newAppt = {
        id: Date.now() + 1, cid: clientId, cn: clientInfo.name,
        treat: selectedService.name, pid: selectedProvider.id, prov: selectedProvider.name,
        date: dateStr, time: selectedTime, dur: selectedService.duration_minutes || 60,
        price: selectedService.price || 0, status: "confirmed", room: "", dep: depositPaid,
        loc: (bookingSettings.spa_name || "Nova Med Spa"), checkin: "upcoming", tip: 0, paid: false,
        notes: clientInfo.notes || "",
        // Audit: who booked this — public booking link
        booked_by_source: "booking_link",
        booked_by_user_name: emailCampaignId() ? "Online (email campaign)" : "Online (booking link)",
        // came from a campaign email's Book Now (?ec=<campaign id>), counted on the campaign
        ...(emailCampaignId() ? { email_campaign_id: emailCampaignId() } : {}),
        booked_at: new Date().toISOString(),
      };
      // ★ DOUBLE-BOOKING GUARD (2026-06-19 Kim Lee / Elba incident): the slot
      //   list the client saw is a browser snapshot and can be stale — another
      //   booking (CRM or online) may have taken this provider's slot while the
      //   page was open. Re-check against the LIVE calendar right before insert.
      if (!(await slotStillFree())) {
        // Rare race: slot was free when checked, taken during payment. The card
        // is already charged — record it (no appointment) so the front desk
        // sees it on the client's billing tab, and tell the client plainly.
        if (depositPaid > 0 && paymentIntentId) {
          await supabase.from("payments").insert([{
            id: Date.now() + 2, client_id: clientId, client_name: clientInfo.name,
            amount: depositPaid, payment_method: "card",
            description: "UNBOOKED online deposit — slot taken during payment (" + selectedService.name + ", " + dateStr + " " + selectedTime + "). Refund or rebook.",
            stripe_payment_intent_id: paymentIntentId, status: "completed",
            created_by_name: "Online (booking link)", created_at: new Date().toISOString(),
          }]);
          setShowPayment(false);
          throw new Error("Sorry — that time was booked by someone else while you were paying. Your $" + depositPaid + " deposit was received and our team will contact you to rebook or refund it. Please do not pay again. Questions: (516) 543-3940.");
        }
        throw new Error("Sorry — that time was just booked. Please go back and choose another time.");
      }

      const { error } = await supabase.from("appointments").insert([newAppt]);
      if (error) throw new Error("Failed to book: " + error.message);

      if (depositPaid > 0 && paymentIntentId) {
        await supabase.from("payments").insert([{
          id: Date.now() + 2,
          client_id: clientId,
          // ★ Deposits taken online used to save no client_name, so every one
          //   of them showed as "Guest" in Payments and on the receipt while
          //   staff-taken deposits showed the real name. Carry the name (and
          //   the appointment it belongs to) the way the CRM does.
          client_name: clientInfo.name,
          appointment_id: newAppt.id,
          amount: depositPaid,
          payment_method: "card",
          description: "Online Deposit - " + selectedService.name,
          stripe_payment_intent_id: paymentIntentId,
          status: "completed",
          created_by_name: "Online (booking link)",
          created_at: new Date().toISOString(),
        }]);
        
        // Auto-send receipt for online deposit
        if (clientInfo.email) {
          try {
            await supabase.functions.invoke("send-receipt", {
              body: {
                to_email: clientInfo.email,
                to_name: clientInfo.name,
                amount: depositPaid,
                service: "Deposit - " + selectedService.name,
                provider: selectedProvider.name,
                date: selectedDate.toLocaleDateString("en-US", {month:"long",day:"numeric",year:"numeric"}),
                payment_method: "Card",
                last4: "",
                transaction_id: paymentIntentId,
                notes: "Deposit for appointment on " + selectedDate.toLocaleDateString() + " at " + selectedTime,
              }
            });
          } catch (recErr) { console.error("Receipt failed:", recErr); }
        }
      }

      // Notify business owner(s) via SMS about the new booking
      try {
        await supabase.functions.invoke("notify-new-booking", {
          body: {
            client_name: clientInfo.name,
            client_phone: clientInfo.phone,
            client_email: clientInfo.email,
            client_notes: clientInfo.notes || "",
            service_name: selectedService.name,
            provider_name: selectedProvider.name,
            date: selectedDate.toLocaleDateString("en-US", {weekday:"long",month:"long",day:"numeric",year:"numeric"}),
            time: getAvailableTimes().find(t=>t.value===selectedTime)?.display || selectedTime,
            duration: selectedService.duration_minutes || 60,
            price: selectedService.price || 0,
            deposit_paid: depositPaid || 0,
            spa_name: (bookingSettings.spa_name || "Nova Med Spa"),
            booking_source: "Public Booking Link",
            booked_by: "Client (self-serve)",
          }
        });
      } catch (notifyErr) { console.error("Booking notification SMS failed:", notifyErr); }

      // ★ Booking confirmation SMS + Email to the CLIENT — re-enabled
      //   2026-06-10. send-lead-sms expects a finished message string +
      //   phone, so we build the text here. Fire-and-forget — a failure
      //   to send can't undo the appointment.
      if (clientInfo.phone) {
        try {
          const _firstName = (clientInfo.name || "").split(" ")[0] || "there";
          const _niceDate = selectedDate.toLocaleDateString("en-US", {weekday:"long", month:"long", day:"numeric"});
          const _niceTime = getAvailableTimes().find(t=>t.value===selectedTime)?.display || selectedTime;
          const _spa = (bookingSettings.spa_name || "Nova Med Spa");
          const _msg = `Hi ${_firstName}! Your ${selectedService.name} with ${selectedProvider.name} is confirmed for ${_niceDate} at ${_niceTime}. Reply C to confirm, or call (516) 543-3940 to reschedule. — ${_spa}`;
          await supabase.functions.invoke("send-lead-sms", {
            body: {
              phone: clientInfo.phone,
              client_id: pendingClientId || null,
              message: _msg,
            }
          });
        } catch (e) { console.error("Client confirmation SMS failed:", e); }
      }
      if (clientInfo.email) {
        try {
          await supabase.functions.invoke("send-appointment-email", {
            body: {
              type: "confirmation",
              to_email: clientInfo.email,
              to_name: clientInfo.name,
              service: selectedService.name,
              provider: selectedProvider.name,
              date: selectedDate.toLocaleDateString("en-US", {weekday:"long",month:"long",day:"numeric",year:"numeric"}),
              time: getAvailableTimes().find(t=>t.value===selectedTime)?.display || selectedTime,
              location: (bookingSettings.spa_name || "Nova Med Spa"),
              deposit_paid: depositPaid || 0,
            }
          });
        } catch (e) { console.error("Client confirmation email failed:", e); }
      }

      // ★ Fire pre-care/post-care immediately for any rule with
      //   trigger_event="on_booking". The cron also picks it up on its 15-min
      //   cycle, but this makes it feel instant. Idempotent — the
      //   treatment_message_logs UNIQUE index prevents doubles.
      try {
        supabase.functions.invoke("pre-post-cron", { body: {} });
      } catch (e) { console.error("pre-post-cron fast-fire failed:", e); }

      setShowPayment(false);
      setSuccess(true);
      setSubmitting(false);
    } catch (e) {
      setErrorMsg(e.message);
      setSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    await createAppointment(pendingClientId, selectedService.deposit_amount, paymentIntent.id);
  };
  if (loading) return (
    <div style={{minHeight:"100vh",background:C.bg,fontFamily:F}}>
      <style>{`@keyframes shimmer { 0% { background-position: -400px 0 } 100% { background-position: 400px 0 } }`}</style>
      <div style={{height:280,background:"linear-gradient(110deg, "+C.cream+" 8%, #efe7d8 18%, "+C.cream+" 33%)",backgroundSize:"800px 100%",animation:"shimmer 1.6s linear infinite"}}/>
      <div style={{maxWidth:800,margin:"0 auto",padding:"30px 20px"}}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{height:70,marginBottom:14,borderRadius:14,background:"linear-gradient(110deg, "+C.surface+" 8%, "+C.light+" 18%, "+C.surface+" 33%)",backgroundSize:"800px 100%",animation:"shimmer 1.6s linear infinite",animationDelay:(i*0.12)+"s"}}/>
        ))}
      </div>
    </div>
  );

  if (success) {
    const downloadICS = () => {
      const dateStr = selectedDate.toISOString().split("T")[0].replace(/-/g, "");
      const [h, m] = selectedTime.split(":").map(Number);
      const startDt = new Date(selectedDate);
      startDt.setHours(h, m, 0, 0);
      const endDt = new Date(startDt.getTime() + (selectedService.duration_minutes || 60) * 60000);
      const fmt = (d) => d.toISOString().replace(/[-:]/g,"").split(".")[0] + "Z";
      const ics = [
        "BEGIN:VCALENDAR","VERSION:2.0","PRODID:-//Elyon//Booking//EN","BEGIN:VEVENT",
        "UID:"+Date.now()+"@elyonhq.com",
        "DTSTAMP:"+fmt(new Date()),
        "DTSTART:"+fmt(startDt),
        "DTEND:"+fmt(endDt),
        "SUMMARY:"+(selectedService.name)+" with "+selectedProvider.name,
        "LOCATION:"+(bookingSettings.spa_name||"Nova Med Spa")+(bookingSettings.address?", "+bookingSettings.address:""),
        "DESCRIPTION:Appointment booked at "+(bookingSettings.spa_name||"Nova Med Spa"),
        "END:VEVENT","END:VCALENDAR"
      ].join("\r\n");
      const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "appointment.ics";
      a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:F}}>
        <div style={{background:C.surface,borderRadius:20,padding:"48px 36px",maxWidth:520,width:"100%",textAlign:"center",boxShadow:"0 20px 60px rgba(180,140,60,0.18)"}}>
          <div style={{width:80,height:80,borderRadius:"50%",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",margin:"0 auto 20px",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 8px 24px rgba(180,140,60,0.35)"}}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <div style={{fontSize:30,fontWeight:800,color:C.text,fontFamily:S,marginBottom:8,letterSpacing:"-0.01em"}}>You're Booked</div>
          <div style={{fontSize:14,color:C.muted,marginBottom:28,lineHeight:1.6}}>Thank you, {clientInfo.name}. Your appointment is confirmed and a text confirmation is on its way.</div>
          <div style={{padding:24,background:"linear-gradient(180deg,"+C.goldBg+", #fff)",borderRadius:16,border:"1px solid "+C.borderGold,marginBottom:24,textAlign:"left"}}>
            <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",fontWeight:700,marginBottom:4,letterSpacing:"0.08em"}}>Service</div>
            <div style={{fontSize:17,fontWeight:700,color:C.text,marginBottom:14,fontFamily:S}}>{selectedService.name}</div>
            <div style={{display:"flex",gap:20,marginBottom:14}}>
              <div style={{flex:1}}>
                <div style={{fontSize:10,color:C.muted,textTransform:"uppercase",fontWeight:700,marginBottom:4,letterSpacing:"0.08em"}}>Provider</div>
                <div style={{fontSize:13,color:C.text,fontWeight:600}}>{selectedProvider.name}</div>
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:10,color:C.muted,textTransform:"uppercase",fontWeight:700,marginBottom:4,letterSpacing:"0.08em"}}>Duration</div>
                <div style={{fontSize:13,color:C.text,fontWeight:600}}>{selectedService.duration_minutes||60} min</div>
              </div>
            </div>
            <div style={{fontSize:10,color:C.muted,textTransform:"uppercase",fontWeight:700,marginBottom:4,letterSpacing:"0.08em"}}>When</div>
            <div style={{fontSize:14,color:C.text,fontWeight:600}}>{selectedDate.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})} at {getAvailableTimes().find(t=>t.value===selectedTime)?.display || selectedTime}</div>
            {selectedService.requires_deposit && selectedService.deposit_amount > 0 && (
              <div style={{marginTop:16,paddingTop:14,borderTop:"1px solid "+C.borderGold}}>
                <div style={{fontSize:10,color:C.muted,textTransform:"uppercase",fontWeight:700,marginBottom:4,letterSpacing:"0.08em"}}>Deposit</div>
                <div style={{fontSize:14,color:"#2e8b57",fontWeight:700}}>${selectedService.deposit_amount} required at appointment</div>
              </div>
            )}
          </div>
          <button onClick={downloadICS} style={{width:"100%",padding:"14px",background:C.surface,color:C.text,border:"1.5px solid "+C.gold,borderRadius:12,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:F,marginBottom:24,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            Add to Calendar
          </button>
          <div style={{paddingTop:18,borderTop:"1px solid "+C.border}}>
            <div style={{fontSize:14,color:C.gold,fontWeight:800,fontFamily:S}}>{bookingSettings.spa_name||"Nova Med Spa"}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:6}}>{bookingSettings.address||"1451 Old Country Rd, Plainview NY"} · {bookingSettings.phone||"(516) 543-3940"}</div>
          </div>
        </div>
      </div>
    );
  }

  const renderCalendar = () => {
    const year = calMonth.getFullYear();
    const month = calMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month+1, 0);
    const startDow = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const today = new Date();
    today.setHours(0,0,0,0);
    
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    
    const prevMonth = () => {
      const n = new Date(calMonth); n.setMonth(n.getMonth()-1); setCalMonth(n);
    };
    const nextMonth = () => {
      const n = new Date(calMonth); n.setMonth(n.getMonth()+1); setCalMonth(n);
    };
    const now = new Date();
    const canGoBack = calMonth.getFullYear() > now.getFullYear() || (calMonth.getFullYear()===now.getFullYear() && calMonth.getMonth() > now.getMonth());

    return (
      <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:12,padding:16,maxWidth:480,margin:"0 auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <button onClick={prevMonth} disabled={!canGoBack} style={{padding:"6px 12px",background:canGoBack?C.goldBg:C.light,color:canGoBack?C.gold:C.dim,border:"1px solid "+(canGoBack?C.borderGold:C.border),borderRadius:8,cursor:canGoBack?"pointer":"not-allowed",fontSize:14,fontWeight:700,fontFamily:F}}>←</button>
          <div style={{fontSize:16,fontWeight:800,color:C.gold,fontFamily:S}}>{MONTHS[month]} {year}</div>
          <button onClick={nextMonth} style={{padding:"6px 12px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:8,cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:F}}>→</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:6}}>
          {DOW_SHORT.map(d => <div key={d} style={{textAlign:"center",fontSize:10,fontWeight:800,color:C.muted,textTransform:"uppercase",padding:"6px 0"}}>{d}</div>)}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
          {cells.map((d, i) => {
            if (!d) return <div key={i}></div>;
            const isPast = d < today;
            const available = !isPast && dayHasAvailability(d);
            const isSelected = selectedDate && d.toDateString() === selectedDate.toDateString();
            const isToday = d.toDateString() === today.toDateString();
            return (
              <button key={i} onClick={()=>{if(available){setSelectedDate(d);setSelectedProvider(null);setSelectedTime(null);setStep(3);}}} disabled={!available} style={{
                aspectRatio:"1",background:isSelected?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":(available?"#fff":C.light),
                color:isSelected?"#fff":(available?C.text:C.dim),
                border:"1.5px solid "+(isSelected?C.gold:(isToday?C.gold:(available?C.border:"transparent"))),
                borderRadius:8,cursor:available?"pointer":"not-allowed",
                fontSize:14,fontWeight:isSelected||isToday?800:500,fontFamily:F,
                display:"flex",alignItems:"center",justifyContent:"center",
                transition:"all 0.15s",padding:0,
                opacity:available?1:0.4
              }} onMouseEnter={e=>{if(available&&!isSelected){e.currentTarget.style.background=C.goldBg;e.currentTarget.style.borderColor=C.gold;}}} onMouseLeave={e=>{if(available&&!isSelected){e.currentTarget.style.background=C.surface;e.currentTarget.style.borderColor=isToday?C.gold:C.border;}}}>
                {d.getDate()}
              </button>
            );
          })}
        </div>
        <div style={{marginTop:14,padding:"10px 12px",background:C.goldBg,borderRadius:8,fontSize:11,color:C.muted,textAlign:"center"}}>
          💡 Gray dates = no availability · <span style={{color:C.gold,fontWeight:700}}>Gold border</span> = today
        </div>
      </div>
    );
  };

  if (showPayment && paymentIntentSecret) {
    return (
      <div style={{minHeight:"100vh",background:C.bg,fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
        <div style={{background:C.surface,borderRadius:16,padding:30,maxWidth:480,width:"100%",boxShadow:"0 8px 32px rgba(180,140,60,0.2)"}}>
          <div style={{textAlign:"center",marginBottom:20}}>
            <div style={{fontSize:32,marginBottom:8}}>💳</div>
            <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:4}}>Pay Deposit</div>
            <div style={{fontSize:13,color:C.muted}}>Secure payment to confirm your appointment</div>
          </div>
          
          <div style={{padding:14,background:C.goldBg,borderRadius:10,marginBottom:20,border:"1px solid "+C.borderGold}}>
            <div style={{fontSize:10,color:C.muted,textTransform:"uppercase",fontWeight:700,marginBottom:4}}>Booking</div>
            <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:2}}>{selectedService.name}</div>
            <div style={{fontSize:12,color:C.muted,marginBottom:2}}>with {selectedProvider.name}</div>
            <div style={{fontSize:12,color:C.muted,marginBottom:8}}>{selectedDate.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})} at {getAvailableTimes().find(t=>t.value===selectedTime)?.display}</div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:8,borderTop:"1px solid "+C.borderGold}}>
              <div style={{fontSize:12,color:C.muted,fontWeight:600}}>Deposit Amount</div>
              <div style={{fontSize:20,fontWeight:800,color:C.gold}}>${selectedService.deposit_amount}</div>
            </div>
          </div>

          <Elements stripe={stripePromise} options={{clientSecret:paymentIntentSecret,locale:"en",appearance:{theme:"stripe",variables:{colorPrimary:"#b48c3c"}}}}>
            <DepositPaymentForm 
              amount={selectedService.deposit_amount}
              description={"Deposit for "+selectedService.name}
              onSuccess={handlePaymentSuccess}
              onError={(msg)=>setErrorMsg(msg)}
            />
          </Elements>

          <button onClick={()=>{setShowPayment(false);setPaymentIntentSecret(null);setSubmitting(false);}} style={{width:"100%",marginTop:12,padding:"10px",background:"transparent",color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>

          <div style={{fontSize:10,color:C.muted,textAlign:"center",marginTop:14}}>🔒 Secure payment powered by Stripe</div>
        </div>
      </div>
    );
  }

  const heroImg = bookingSettings.hero_image_url;
  // Hero gradient matched to the warm dark palette (was navy-blue, clashed
  // with the new charcoal-plum body). Now: deep plum-black → warm gold edge.
  const heroBg = heroImg
    ? `linear-gradient(180deg, rgba(13,9,15,0.55), rgba(13,9,15,0.78)), url(${heroImg})`
    : `linear-gradient(135deg, ${C.pageBg} 0%, #2a212c 45%, ${C.goldD} 130%)`;

  return (
    <div style={{
      minHeight:"100vh",
      // Warm deep charcoal-plum base + a soft gold ambient glow up top so the
      // page has depth and warmth instead of a flat harsh black. Fixed so the
      // glow stays anchored as the client scrolls.
      background:`radial-gradient(120% 60% at 50% 0%, rgba(201,163,109,0.10) 0%, rgba(201,163,109,0) 42%), linear-gradient(180deg, ${C.pageBg} 0%, ${C.pageBg2} 100%)`,
      backgroundAttachment:"fixed",
      fontFamily:F, paddingBottom:80,
    }}>
      {/* HERO */}
      <div style={{background:heroBg,backgroundSize:"cover",backgroundPosition:"center",padding:"56px 20px 64px",textAlign:"center",position:"relative",borderBottom:"4px solid "+C.gold}}>
        <div style={{maxWidth:720,margin:"0 auto"}}>
          {bookingSettings.logo_url && (
            <img src={bookingSettings.logo_url} alt="" style={{height:54,marginBottom:14,filter:"drop-shadow(0 4px 12px rgba(0,0,0,0.3))"}}/>
          )}
          <div style={{fontSize:11,color:C.gold,letterSpacing:"0.3em",textTransform:"uppercase",fontWeight:700,marginBottom:14,opacity:0.95}}>Book Your Visit</div>
          <div style={{fontSize:"clamp(34px, 6vw, 54px)",fontWeight:700,color:"#fff",fontFamily:S,lineHeight:1.05,letterSpacing:"-0.01em",marginBottom:14,textShadow:"0 4px 16px rgba(0,0,0,0.4)"}}>
            {bookingSettings.spa_name||"Nova Med Spa"}
          </div>
          {bookingSettings.hero_subtitle && (
            <div style={{fontSize:15,color:"rgba(255,255,255,0.85)",fontStyle:"italic",fontFamily:S,marginBottom:18,maxWidth:520,margin:"0 auto 18px",lineHeight:1.5}}>
              {bookingSettings.hero_subtitle}
            </div>
          )}
          <div style={{display:"inline-flex",alignItems:"center",gap:10,padding:"8px 18px",background:"rgba(255,255,255,0.12)",backdropFilter:"blur(10px)",borderRadius:99,border:"1px solid rgba(255,255,255,0.2)"}}>
            <div style={{display:"flex",gap:1}}>
              {[1,2,3,4,5].map(i => (
                <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill={C.gold} stroke="none"><path d="M12 2l3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z"/></svg>
              ))}
            </div>
            <div style={{fontSize:11,color:"#fff",fontWeight:600,letterSpacing:"0.04em"}}>Trusted by 500+ clients · 5-star rated</div>
          </div>
        </div>
      </div>

      <div style={{maxWidth:820,margin:"-32px auto 0",padding:"0 16px",position:"relative",zIndex:2}}>
        {/* Progress card */}
        <div style={{background:C.surface,borderRadius:16,padding:"20px 20px 24px",boxShadow:"0 10px 30px rgba(180,140,60,0.12)",marginBottom:28,border:"1px solid "+C.border}}>
          <div style={{display:"flex",gap:6}}>
            {["Service","Date","Provider","Time & Info"].map((label, i) => (
              <div key={i} style={{flex:1,textAlign:"center"}}>
                <div style={{height:3,background:step > i ? C.gold : C.border,borderRadius:2,marginBottom:8,transition:"all 0.3s"}}></div>
                <div style={{fontSize:10,color:step > i ? C.gold : C.muted,fontWeight:step > i ? 800 : 500,textTransform:"uppercase",letterSpacing:"0.08em"}}>{i+1}. {label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ★ TRUST STRIP — 3 reassurance bullets, shown on the service step.
            Sets the "instant, professional, convenient" tone before the
            client commits. Wraps gracefully on phones. */}
        {step === 1 && (
          <div style={{display:"flex",flexWrap:"wrap",justifyContent:"center",gap:"10px 22px",marginBottom:26}}>
            {[
              "Instant Confirmation",
              "Licensed Medical Providers",
              "Convenient Plainview Location",
            ].map((t) => (
              <div key={t} style={{display:"inline-flex",alignItems:"center",gap:7,fontSize:12.5,fontWeight:600,color:C.onDark}}>
                <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:18,height:18,borderRadius:"50%",background:C.goldBg,border:"1px solid "+C.borderGold,flexShrink:0}}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 6"/></svg>
                </span>
                {t}
              </div>
            ))}
          </div>
        )}

        {/* ★ CLINIC PHOTO — luxurious treatment-room shot. Pulls from
            booking_settings.clinic_photo_url (set in Page Customization);
            falls back to hero_image_url. Hidden if neither is set. */}
        {step === 1 && (bookingSettings.clinic_photo_url || bookingSettings.hero_image_url) && (
          <div style={{borderRadius:16,overflow:"hidden",marginBottom:26,boxShadow:"0 10px 30px rgba(180,140,60,0.14)",border:"1px solid "+C.border,position:"relative"}}>
            <img src={bookingSettings.clinic_photo_url || bookingSettings.hero_image_url} alt="Our clinic" style={{width:"100%",maxHeight:300,objectFit:"cover",display:"block"}}/>
            <div style={{position:"absolute",inset:0,background:"linear-gradient(180deg,rgba(0,0,0,0) 55%,rgba(0,0,0,0.45))"}}/>
            <div style={{position:"absolute",bottom:14,left:18,color:"#fff",fontFamily:S,fontSize:15,fontWeight:600,letterSpacing:"0.02em",textShadow:"0 2px 8px rgba(0,0,0,0.5)"}}>
              {bookingSettings.spa_name || "Nova Med Spa"} · Plainview, NY
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <div style={{fontSize:28,fontWeight:700,color:C.onDark,fontFamily:S,marginBottom:8,textAlign:"center",letterSpacing:"-0.01em"}}>
              {embedCategory || "Select Your Service & Preferred Time"}
            </div>
            <div style={{fontSize:13,color:C.onDarkMuted,textAlign:"center",marginBottom:32}}>
              {embedCategory ? "Choose your treatment, then pick a time that works for you" : "Tap a category to see our treatments"}
            </div>

            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {CAT_ORDER.map(cat => {
                // ★ Embed mode — when ?category= deep-linked, show ONLY that
                //   category so the client sees just those treatments.
                if (embedCategory && cat !== embedCategory) return null;
                const svcs = servicesByCategory[cat];
                if (!svcs || svcs.length === 0) return null;
                const isSpecials = cat === "Specials";
                // Specials auto-opens so the promo is visible without a tap.
                // An embed-targeted category also auto-opens.
                const isOpen = openCategory === cat || (isSpecials && openCategory === null) || (embedCategory === cat);
                const color = CAT_COLORS[cat] || C.gold;
                return (
                  <div key={cat} style={{
                    background: isSpecials ? "linear-gradient(135deg, #fff 0%, rgba(225,29,72,0.04) 100%)" : "#fff",
                    border:"1.5px solid "+(isSpecials ? color : (isOpen?color:C.border)),
                    borderRadius:14,
                    overflow:"hidden",
                    transition:"all 0.25s",
                    boxShadow: isSpecials ? "0 8px 26px rgba(168,80,106,0.22)" : (isOpen?"0 8px 24px "+color+"22":"0 4px 16px rgba(0,0,0,0.28)"),
                    position:"relative",
                  }}>
                    {isSpecials && (
                      <div style={{position:"absolute",top:10,right:14,fontSize:9.5,fontWeight:800,color:"#fff",background:color,padding:"3px 10px",borderRadius:50,letterSpacing:"0.10em",zIndex:1}}>
                        🔥 LIMITED TIME
                      </div>
                    )}
                    <button onClick={()=>setOpenCategory(isOpen?null:cat)} style={{width:"100%",padding:"22px 24px",background:isOpen?color+"08":"#fff",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",fontFamily:F,textAlign:"left"}}>
                      <div style={{display:"flex",alignItems:"center",gap:16}}>
                        <div style={{width:46,height:46,borderRadius:12,background:color+"12",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,border:"1px solid "+color+"25"}}>
                          <CatIcon cat={cat} color={color} size={24}/>
                        </div>
                        <div>
                          <div style={{fontSize:isSpecials?19:17,fontWeight:isSpecials?800:700,color:isOpen?color:C.text,fontFamily:S,letterSpacing:"-0.005em"}}>{isSpecials ? "✨ Specials" : cat}</div>
                          <div style={{fontSize:11,color:isSpecials?color:C.muted,marginTop:3,letterSpacing:"0.03em",fontWeight:isSpecials?600:400}}>
                            {isSpecials ? `${svcs.length} exclusive offer${svcs.length!==1?"s":""} this month` : `${svcs.length} treatment${svcs.length!==1?"s":""}`}
                          </div>
                        </div>
                      </div>
                      <div style={{fontSize:20,color:isOpen?color:C.dim,transform:isOpen?"rotate(180deg)":"rotate(0)",transition:"transform 0.25s"}}>▾</div>
                    </button>
                    {isOpen && (
                      <div style={{borderTop:"1px solid "+color+"30",padding:8,background:"rgba(0,0,0,0.01)"}}>
                        {svcs.map(s => (
                          <button key={s.id} onClick={async ()=>{setSelectedService(s);setSelectedAddons([]);const {data:addonsList}=await supabase.from("service_addons").select("id").eq("service_id",s.id).eq("is_active",true).limit(1);if(addonsList&&addonsList.length>0){setShowAddonsModal(true);}else{setStep(2);}}} style={{width:"100%",textAlign:"left",padding:"12px 14px",background:C.surface,border:"1px solid "+C.border,borderLeft:"3px solid "+color,borderRadius:8,cursor:"pointer",fontFamily:F,marginBottom:6,transition:"all 0.15s"}} onMouseEnter={e=>{e.currentTarget.style.background=color+"08";e.currentTarget.style.borderLeftWidth="5px";}} onMouseLeave={e=>{e.currentTarget.style.background=C.surface;e.currentTarget.style.borderLeftWidth="3px";}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:2}}>{s.name}</div>
                                {s.description && <div style={{fontSize:11,color:C.muted,lineHeight:1.4}}>{s.description}</div>}
                              </div>
                              <div style={{textAlign:"right",flexShrink:0}}>
                                {s.hide_price ? <div style={{fontSize:10,color:C.muted,fontStyle:"italic"}}>Call for pricing</div> : (s.price > 0 ? <div style={{fontSize:14,fontWeight:800,color:color}}>${s.price}{s.price_max && s.price_max > s.price ? "+" : ""}</div> : <div style={{fontSize:10,color:C.muted,fontStyle:"italic"}}>Call</div>)}
                                <div style={{fontSize:10,color:C.muted,marginTop:2}}>{s.duration_minutes} min</div>
                                {s.requires_deposit && s.deposit_amount > 0 && (<div style={{fontSize:9,color:"#2e8b57",fontWeight:700,marginTop:3,padding:"2px 6px",background:"rgba(46,139,87,0.12)",borderRadius:4}}>${s.deposit_amount} deposit</div>)}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <button onClick={()=>setStep(1)} style={{padding:"6px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:11,color:C.muted,cursor:"pointer",fontFamily:F,marginBottom:14}}>← Back</button>
            <div style={{fontSize:22,fontWeight:800,color:C.onDark,fontFamily:S,marginBottom:6,textAlign:"center"}}>Pick a Date</div>
            <div style={{fontSize:13,color:C.onDarkMuted,textAlign:"center",marginBottom:20}}>for {selectedService.name} · {selectedService.duration_minutes} min</div>
            {renderCalendar()}
          </div>
        )}

        {step === 3 && (
          <div>
            <button onClick={()=>setStep(2)} style={{padding:"6px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:11,color:C.muted,cursor:"pointer",fontFamily:F,marginBottom:14}}>← Back</button>
            <div style={{fontSize:22,fontWeight:800,color:C.onDark,fontFamily:S,marginBottom:6,textAlign:"center"}}>Choose a Provider</div>
            <div style={{fontSize:13,color:C.onDarkMuted,textAlign:"center",marginBottom:24}}>available on {selectedDate.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}</div>
            
            {getProvidersForDate().length === 0 && (
              <div style={{padding:40,textAlign:"center",color:C.muted,background:C.surface,borderRadius:12}}>No providers available on this date. Please pick another day.</div>
            )}
            
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:16}}>
              {getProvidersForDate().map(p => (
                <button key={p.id} onClick={()=>{setSelectedProvider(p);setStep(4);}} style={{textAlign:"center",padding:"28px 20px 22px",background:C.surface,border:"1.5px solid "+C.border,borderRadius:14,cursor:"pointer",fontFamily:F,transition:"all 0.25s",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",position:"relative",overflow:"hidden"}} onMouseEnter={e=>{e.currentTarget.style.borderColor=p.color||C.gold;e.currentTarget.style.transform="translateY(-4px)";e.currentTarget.style.boxShadow="0 12px 28px rgba(180,140,60,0.18)";}} onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 1px 3px rgba(0,0,0,0.04)";}}>
                  <div style={{position:"absolute",top:0,left:0,right:0,height:4,background:"linear-gradient(90deg,"+(p.color||C.gold)+", "+C.goldD+")"}}/>
                  {p.photo_url ? (
                    <img src={p.photo_url} alt={p.name} style={{width:96,height:96,borderRadius:"50%",objectFit:"cover",border:"3px solid "+(p.color||C.gold),margin:"0 auto 14px",boxShadow:"0 6px 18px "+( p.color||C.gold)+"30",display:"block"}}/>
                  ) : (
                    <div style={{width:96,height:96,borderRadius:"50%",background:"linear-gradient(135deg, "+(p.color||C.gold)+"25, "+(p.color||C.gold)+"10)",border:"3px solid "+(p.color||C.gold),display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,fontWeight:700,color:p.color||C.gold,margin:"0 auto 14px",fontFamily:S,boxShadow:"0 6px 18px "+(p.color||C.gold)+"30"}}>
                      {(p.name||"").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
                    </div>
                  )}
                  <div style={{fontSize:17,fontWeight:700,color:C.text,fontFamily:S,marginBottom:4,letterSpacing:"-0.005em"}}>{p.name}</div>
                  <div style={{fontSize:10,color:p.color||C.gold,fontWeight:700,marginBottom:10,letterSpacing:"0.12em",textTransform:"uppercase"}}>{p.title || "Provider"}</div>
                  {p.bio && <div style={{fontSize:12,color:C.muted,lineHeight:1.5,fontStyle:"italic",fontFamily:S,paddingTop:10,borderTop:"1px solid "+C.border}}>{p.bio}</div>}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <button onClick={()=>setStep(3)} style={{padding:"6px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:11,color:C.muted,cursor:"pointer",fontFamily:F,marginBottom:14}}>← Back</button>
            <div style={{fontSize:22,fontWeight:800,color:C.onDark,fontFamily:S,marginBottom:6,textAlign:"center"}}>Pick Time & Confirm</div>
            <div style={{fontSize:13,color:C.onDarkMuted,textAlign:"center",marginBottom:20}}>{selectedService.name} with {selectedProvider.name} on {selectedDate.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}</div>
            
            {!selectedTime && (
              <div style={{maxWidth:440,margin:"0 auto"}}>
                <div style={{fontSize:11,fontWeight:800,color:C.gold,textTransform:"uppercase",marginBottom:10,textAlign:"center"}}>Available Times</div>
                {getAvailableTimes().length === 0 ? (
                  <div style={{padding:20,textAlign:"center",color:C.muted,fontSize:12,background:C.surface,borderRadius:8}}>No available times this day. Please pick another date.</div>
                ) : (
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
                    {getAvailableTimes().map(t => (
                      <button key={t.value} onClick={()=>setSelectedTime(t.value)} style={{padding:"12px",background:C.surface,color:C.text,border:"1.5px solid "+C.border,borderRadius:8,cursor:"pointer",fontFamily:F,fontSize:13,fontWeight:600,transition:"all 0.15s"}} onMouseEnter={e=>{e.currentTarget.style.background=C.goldBg;e.currentTarget.style.borderColor=C.gold;e.currentTarget.style.color=C.gold;}} onMouseLeave={e=>{e.currentTarget.style.background=C.surface;e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.text;}}>
                        {t.display}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedTime && (
              <div style={{maxWidth:440,margin:"0 auto"}}>
                <div style={{padding:16,background:C.goldBg,borderRadius:10,border:"1px solid "+C.borderGold,marginBottom:20}}>
                  <div style={{fontSize:10,color:C.muted,textTransform:"uppercase",fontWeight:700,marginBottom:4}}>Booking Summary</div>
                  <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:2}}>{selectedService.name}</div>
                  <div style={{fontSize:12,color:C.muted,marginBottom:2}}>with {selectedProvider.name}</div>
                  <div style={{fontSize:12,color:C.muted,marginBottom:6}}>{selectedDate.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})} at {getAvailableTimes().find(t=>t.value===selectedTime)?.display}</div>
                  <button onClick={()=>setSelectedTime(null)} style={{fontSize:11,color:C.gold,background:"none",border:"none",cursor:"pointer",textDecoration:"underline",padding:0,fontFamily:F,fontWeight:600}}>Change time</button>
                  {selectedService.price > 0 && <div style={{fontSize:14,fontWeight:800,color:C.gold,marginTop:6}}>${selectedService.price}</div>}
                  {selectedService.requires_deposit && selectedService.deposit_amount > 0 && (
                    <div style={{marginTop:10,padding:"8px 12px",background:"rgba(46,139,87,0.12)",border:"1px solid rgba(46,139,87,0.3)",borderRadius:8,fontSize:12,color:"#2e8b57",fontWeight:700,textAlign:"center"}}>
                      💳 A ${selectedService.deposit_amount} deposit is required to confirm this booking
                    </div>
                  )}
                </div>
                
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  <div>
                    <label style={{fontSize:11,fontWeight:800,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Full Name *</label>
                    <input value={clientInfo.name} onChange={e=>setClientInfo({...clientInfo,name:e.target.value})} placeholder="Jane Smith" style={{width:"100%",padding:"12px 14px",background:C.surface,border:"1px solid "+C.border,borderRadius:8,fontSize:14,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    <div>
                      <label style={{fontSize:11,fontWeight:800,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Phone *</label>
                      <input value={clientInfo.phone} onChange={e=>setClientInfo({...clientInfo,phone:e.target.value})} placeholder="(555) 123-4567" style={{width:"100%",padding:"12px 14px",background:C.surface,border:"1px solid "+C.border,borderRadius:8,fontSize:14,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:800,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Email *</label>
                      <input type="email" value={clientInfo.email} onChange={e=>setClientInfo({...clientInfo,email:e.target.value})} placeholder="jane@email.com" style={{width:"100%",padding:"12px 14px",background:C.surface,border:"1px solid "+C.border,borderRadius:8,fontSize:14,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
                    </div>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:800,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Notes (optional)</label>
                    <textarea value={clientInfo.notes} onChange={e=>setClientInfo({...clientInfo,notes:e.target.value})} rows={2} placeholder="Anything we should know?" style={{width:"100%",padding:"12px 14px",background:C.surface,border:"1px solid "+C.border,borderRadius:8,fontSize:13,fontFamily:F,outline:"none",resize:"vertical",boxSizing:"border-box"}}/>
                  </div>
                </div>

                {errorMsg && <div style={{marginTop:14,padding:"10px 14px",background:C.redL,color:C.red,borderRadius:8,fontSize:12,fontWeight:600}}>{errorMsg}</div>}

                <button onClick={submit} disabled={submitting||!clientInfo.name||!clientInfo.phone||!clientInfo.email} style={{marginTop:20,width:"100%",padding:"14px",background:(submitting||!clientInfo.name||!clientInfo.phone||!clientInfo.email)?C.light:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:(submitting||!clientInfo.name||!clientInfo.phone||!clientInfo.email)?C.muted:"#fff",border:"none",borderRadius:10,fontSize:15,fontWeight:700,cursor:(submitting||!clientInfo.name||!clientInfo.phone||!clientInfo.email)?"not-allowed":"pointer",fontFamily:F,letterSpacing:"0.02em"}}>
                  {submitting?"Processing...":(selectedService.requires_deposit && selectedService.deposit_amount > 0 ? "💳 Pay $"+selectedService.deposit_amount+" & Confirm" : "✨ Confirm Booking")}
                </button>
                
                <div style={{fontSize:12,color:"#fff",fontWeight:600,textAlign:"center",marginTop:14,lineHeight:1.55}}>
                  By clicking "Book Appointment" and providing your mobile number, you agree to receive SMS messages from Nova Med Spa regarding your appointments and related updates. Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase. By continuing, you agree to our Privacy Policy and Terms &amp; Conditions.
                  <br/><br/>
                  <span style={{color:"rgba(255,255,255,0.75)",fontWeight:500}}>By booking, you agree to our cancellation policy. Need help? Call (516) 543-3940</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    
      {showAddonsModal && selectedService && (
        <AddonsModal
          service={selectedService}
          onContinue={(chosen) => {
            setSelectedAddons(chosen);
            setShowAddonsModal(false);
            setStep(2);
          }}
          onBack={() => {
            setShowAddonsModal(false);
            setSelectedService(null);
          }}
        />
      )}
      </div>
  );
}
